// @spec SHL-STR-016
'use client';

import { Command } from 'cmdk';
import { Clock, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface RecentSearchesProps {
  /** Recent search terms */
  searches: string[];
  /** Called when a recent search is selected */
  onSelect: (search: string) => void;
  /** Called when a recent search is removed */
  onRemove: (search: string) => void;
  /** Called when all recent searches are cleared */
  onClearAll: () => void;
  /** Optional additional CSS classes */
  className?: string;
}

/**
 * Recent searches list component for the global search dialog.
 *
 * Displays up to 5 most recent searches with:
 * - Clock icon + search term
 * - Remove button (X) on hover
 * - "Clear all" link at bottom
 * - Empty state when no searches exist
 */
export function RecentSearches({
  searches,
  onSelect,
  onRemove,
  onClearAll,
  className,
}: RecentSearchesProps) {
  const { t } = useTranslation('common');
  // Show only the 5 most recent
  const displayedSearches = searches.slice(0, 5);

  if (displayedSearches.length === 0) {
    return (
      <Command.Empty className={cn('py-6 text-center text-sm text-muted-foreground', className)}>
        {t('search.noRecentSearches')}
      </Command.Empty>
    );
  }

  return (
    <Command.Group
      heading={
        <div className="flex items-center justify-between">
          <span>{t('search.recentSearches')}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onClearAll();
            }}
            className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
          >
            {t('table.clearAll')}
          </Button>
        </div>
      }
      className={className}
    >
      {displayedSearches.map((search) => (
        <Command.Item
          key={search}
          value={search}
          onSelect={() => {
            onSelect(search);
          }}
          className="group flex cursor-pointer items-center gap-3 rounded-lg px-4 py-3 text-sm aria-selected:bg-accent"
        >
          <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
          <span className="flex-1 truncate">{search}</span>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              onRemove(search);
            }}
            className="h-6 w-6 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
            aria-label={`Remove "${search}" from recent searches`}
          >
            <X className="h-3 w-3" />
          </Button>
        </Command.Item>
      ))}
    </Command.Group>
  );
}
